import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { createFlashDeal, FLASH_DEAL_DURATION_OPTIONS } from "../utils/merchantFlashDealsFirestore";
import { firebaseStorage } from "../firebase/firebaseClient";
import { useResolvedMerchantId } from "../hooks/useResolvedMerchantId";

const MAX_ITEM_CHARS = 80;
const MAX_DEAL_CHARS = 200;

function FlashDealComposer({ onPosted, merchantId: merchantIdProp, merchantName: merchantNameProp }) {
  const { t } = useTranslation();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { merchantId, businessName } = useResolvedMerchantId(userInfo, merchantIdProp, merchantNameProp);

  const [itemName, setItemName] = useState("");
  const [dealText, setDealText] = useState("");
  const [durationDays, setDurationDays] = useState(FLASH_DEAL_DURATION_OPTIONS[1]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const isEmpty = itemName.trim().length === 0 || dealText.trim().length === 0;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    setError("");
    try {
      const path = `flash_deals/${merchantId || "unknown"}/${Date.now()}_${file.name}`;
      const fileRef = storageRef(firebaseStorage, path);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      setPhotoUrl(downloadUrl);
    } catch {
      setError(t("flashDeals.uploadFailed"));
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClearPhoto = () => setPhotoUrl(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEmpty || submitting) return;

    setSubmitting(true);
    setError("");
    setSuccess(false);

    if (!merchantId) {
      setError(t("flashDeals.missingMerchant"));
      setSubmitting(false);
      return;
    }

    try {
      await createFlashDeal(merchantId, businessName, {
        itemName: itemName.trim(),
        dealText: dealText.trim(),
        durationDays,
        photoUrl,
      });
      setItemName("");
      setDealText("");
      setDurationDays(FLASH_DEAL_DURATION_OPTIONS[1]);
      setPhotoUrl(null);
      setSuccess(true);
      if (onPosted) onPosted();
    } catch {
      setError(t("flashDeals.postFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <h6 className="card-title mb-3">{t("flashDeals.newDeal")}</h6>

        {success && (
          <div className="alert alert-success py-2 mb-3" role="status">
            {t("flashDeals.posted")}
          </div>
        )}

        {error && (
          <p className="text-danger small mb-2" role="alert">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <label className="small text-muted mb-1 d-block">{t("flashDeals.item")}</label>
            <input
              type="text"
              className="form-control"
              maxLength={MAX_ITEM_CHARS}
              placeholder={t("flashDeals.itemPlaceholder")}
              value={itemName}
              onChange={(e) => {
                setSuccess(false);
                setItemName(e.target.value);
              }}
              disabled={submitting}
            />
          </div>

          <div className="mb-2">
            <label className="small text-muted mb-1 d-block">{t("flashDeals.deal")}</label>
            <textarea
              className="form-control"
              rows={2}
              maxLength={MAX_DEAL_CHARS}
              placeholder={t("flashDeals.dealPlaceholder")}
              value={dealText}
              onChange={(e) => {
                setSuccess(false);
                setDealText(e.target.value);
              }}
              disabled={submitting}
            />
          </div>

          <div className="mb-3">
            <label className="small text-muted mb-1 d-block">{t("flashDeals.validFor")}</label>
            <select
              className="form-select"
              style={{ maxWidth: "220px" }}
              value={durationDays}
              onChange={(e) => setDurationDays(Number(e.target.value))}
              disabled={submitting}
            >
              {FLASH_DEAL_DURATION_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {t("flashDeals.days", { count: days })}
                </option>
              ))}
            </select>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {photoUrl ? (
            <div className="mb-3 position-relative" style={{ display: "inline-block", width: "100%" }}>
              <img
                src={photoUrl}
                alt={t("flashDeals.imageAlt")}
                className="rounded"
                style={{ width: "100%", maxHeight: "220px", objectFit: "cover" }}
              />
              <button
                type="button"
                className="btn btn-sm btn-dark"
                onClick={handleClearPhoto}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  padding: "2px 8px",
                  fontSize: "0.75rem",
                  opacity: 0.85,
                }}
              >
                × {t("flashDeals.remove")}
              </button>
            </div>
          ) : (
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                style={{ fontSize: "0.75rem" }}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLoading || submitting}
              >
                {uploadLoading ? t("flashDeals.uploading") : t("flashDeals.uploadPhoto")}
              </button>
            </div>
          )}

          <div className="d-flex justify-content-end">
            <button type="submit" className="btn btn-primary btn-sm" disabled={isEmpty || submitting}>
              {submitting ? t("flashDeals.publishing") : t("flashDeals.publish")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FlashDealComposer;
